import { BlockadeTypeId, ExaminersList, ExtendedExaminersList } from './ExaminersStructure.types';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';

import {
  getIsUserKK,
  getIsUserKGZ,
  getIsUserPZE,
  getIsUserCZE,
  getIsUserCZWO,
  getIsUserPZWO,
  AssessmentSessionTokenInfo,
} from '../../hooks/useSessionUser';

export const isExaminingTeamBlocked = (element: ExaminersList): boolean => {
  if (element) {
    return !(
      element && element.inferiors.some((e) => e.examiner?.examinerPermission?.isBlocked === BlockadeTypeId.None && !e.examiner?.examinerPermission?.isVerifier)
    );
  } else return false;
};

export const getAllOpenChildrenCount = (inferiors: ExaminersList[] | null): number => {
  return (
    inferiors?.reduce((result: number, currentValue: ExaminersList) => {
      return result + 1 + (!currentValue.isClosed ? getAllOpenChildrenCount(currentValue.inferiors) : 0);
    }, 0) || 0
  );
};

export const reduceExaminersList = (
  element: ExaminersList,
  limit: number,
  allChildrenCountCountedOutside?: number,
): ExaminersList => {
  const allChildrenCount =
    allChildrenCountCountedOutside !== undefined
      ? allChildrenCountCountedOutside
      : getAllOpenChildrenCount(element.inferiors);

  if (allChildrenCount <= limit) {
    return element;
  }

  let restLimit = limit;

  const mappedInferiors = element.inferiors.map((inferior) => {
    if (restLimit === 0) {
      return null;
    }

    const childrenCount = !inferior.isClosed ? getAllOpenChildrenCount(inferior.inferiors) : 0;
    if (childrenCount <= restLimit) {
      restLimit = restLimit - childrenCount - 1;
      return inferior;
    }
    const reducedList = reduceExaminersList(inferior, restLimit, childrenCount);
    restLimit = 0;
    return reducedList;
  });

  const reducedInferiors = mappedInferiors.filter((item) => item !== null) as ExaminersList[];
  return { ...element, inferiors: reducedInferiors };
};

const isMenuItemKGZ = (examiner: ExaminersList): boolean => examiner?.function === ExaminerFunction.KGZ;

export const isManualLockIcon = (examiner: ExtendedExaminersList): boolean => {
  return examiner.blockadeTypeId === BlockadeTypeId.Manual && !isMenuItemKGZ(examiner);
};

export const isAutoLockIcon = (examiner: ExtendedExaminersList): boolean => {
  return examiner.blockadeTypeId === BlockadeTypeId.Automatic && !isMenuItemKGZ(examiner);
};

export const getCanUserSendMessageTo = (
  element: ExaminersList | ExtendedExaminersList,
  userAssessmentSessionInfo: AssessmentSessionTokenInfo,
  hasPermissionToCommunicationModule: boolean,
): boolean => {
  const isMenuItemKK = element?.function === ExaminerFunction.KK;
  const isMenuItemKGZ = element?.function === ExaminerFunction.KGZ;
  const isMenuItemCZE = element?.function === ExaminerFunction.CZE;
  const isMenuItemCZWO = element?.function === ExaminerFunction.CZWO;
  const isMenuItemPZWO = element?.function === ExaminerFunction.PZWO;

  const isUserKK = getIsUserKK(userAssessmentSessionInfo);
  const isUserKGZ = getIsUserKGZ(userAssessmentSessionInfo);
  const isUserPZE = getIsUserPZE(userAssessmentSessionInfo);
  const isUserCZE = getIsUserCZE(userAssessmentSessionInfo);
  const isUserCZWO = getIsUserCZWO(userAssessmentSessionInfo);
  const isUserPZWO = getIsUserPZWO(userAssessmentSessionInfo);

  return (
    !!element &&
    hasPermissionToCommunicationModule &&
    !!element?.examiner &&
    ((isUserKK && !isMenuItemKK) ||
      (isUserKGZ && !isMenuItemKGZ && ((isUserPZWO && !isMenuItemPZWO) || !isUserPZWO)) ||
      (isUserPZE && isMenuItemCZE) ||
      (isUserPZE && isMenuItemKGZ) ||
      (isUserCZE && !isMenuItemCZE && !isMenuItemCZWO) ||
      (isUserPZWO && isMenuItemCZWO) ||
      (isUserCZWO && isMenuItemPZWO))
  );
};

export const isElementInContainer = (element: HTMLDivElement, container: HTMLDivElement) => {
  const rect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const result =
    Math.max(containerRect.top, rect.top, containerRect.bottom, rect.bottom) -
      Math.min(containerRect.top, rect.top, containerRect.bottom, rect.bottom) <
    containerRect.bottom - containerRect.top + rect.bottom - rect.top;
  return result;
};
